import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { doc, getDoc } from 'firebase/firestore';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { Caption, SubHeading } from '@/components/ui/Text';
import { useMaybeItems } from '@/hooks/useMaybeItems';
import { useUserStore } from '@/state/userStore';
import { useToast } from '@/components/ui/ToastProvider';
import { db } from '@/lib/firebase';
import {
  type MaybeItem,
  type MaybeItemTag,
} from '@/services/maybeItems';
import { syncTripMembership } from '@/services/tripMembership';
import { MaybeItemCard } from './MaybeItemCard';

const TAGS: Array<{ id: MaybeItemTag; label: string; icon: keyof typeof Ionicons.glyphMap }> = [
  { id: 'indoor', label: 'Indoor', icon: 'home-outline' },
  { id: 'quick-stop', label: 'Quick stop', icon: 'flash-outline' },
  { id: 'high-energy', label: 'High energy', icon: 'football-outline' },
  { id: 'quiet', label: 'Quiet', icon: 'leaf-outline' },
];

/** Minimal trip info for resolving "Suggested by" labels */
export type MaybeContainerTripInfo = {
  owner?: string;
  collaborators?: Array<{ userId: string; name?: string; email?: string }>;
};

type MaybeContainerProps = {
  tripId: string;
  canEdit?: boolean;
  /** Trip (or owner + collaborators) for resolving suggestedByLabel per item */
  trip?: MaybeContainerTripInfo | null;
  /** Bumps to force refresh when external flows add to Maybe */
  refreshToken?: number;
  /**
   * Return true if the item was actually scheduled into the itinerary.
   * Return false if the user cancelled the flow (no-op).
   */
  onScheduleItem?: (item: MaybeItem) => Promise<boolean> | boolean;
  onOpenIdeas?: () => void;
};

function getSuggestedByLabel(
  createdBy: string,
  currentUserId: string | null,
  trip: MaybeContainerTripInfo | null | undefined,
  userNames: Record<string, string>,
): string {
  if (createdBy === currentUserId) return 'You';
  if (userNames[createdBy]) return userNames[createdBy];
  if (!trip) return 'Someone';
  if (trip.owner === createdBy) return 'Trip owner';
  const collab = trip.collaborators?.find((c) => c.userId === createdBy);
  if (collab?.name?.trim()) return collab.name.trim();
  if (collab?.email) return collab.email;
  return 'A collaborator';
}

export function MaybeContainer({
  tripId,
  canEdit = false,
  trip = null,
  refreshToken,
  onScheduleItem,
  onOpenIdeas,
}: MaybeContainerProps) {
  const theme = useTheme();
  const { showSuccess, showError, showToast } = useToast();
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const userId = firebaseUser?.uid || null;

  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const userNamesRef = useRef<Record<string, string>>({});
  const [newTitle, setNewTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState<MaybeItemTag[]>([]);
  const [creating, setCreating] = useState(false);

  const {
    items,
    loading,
    addMaybeItem,
    scheduleItem,
    archiveItem,
    isItemCreating,
    isItemScheduling,
    isItemArchiving,
    isOnline,
  } = useMaybeItems({ tripId, userId, status: 'pending', refreshToken });


  // Ensure trip has members/memberIds so voting and subcollections work (runs once when Maybe is shown).
  useEffect(() => {
    if (!tripId || !userId) return;
    void syncTripMembership(tripId).catch(() => {});
  }, [tripId, userId]);


  // Resolve userIds -> display names (used for "Suggested by Alex", including trip owner).
  // Do NOT include userNames in deps: this effect updates userNames; a ref is used to compute "missing" without causing a loop.
  useEffect(() => {
    const ids = Array.from(
      new Set(
        [
          ...(items.map((i) => i.createdBy).filter(Boolean) as string[]),
          ...(trip?.owner ? [trip.owner] : []),
        ].filter(Boolean),
      ),
    );
    if (ids.length === 0) return;

    // Seed from collaborator names (cheap, avoids extra reads)
    const collaboratorNameMap: Record<string, string> = {};
    for (const c of trip?.collaborators ?? []) {
      if (c?.userId && c?.name?.trim()) {
        collaboratorNameMap[c.userId] = c.name.trim();
      }
    }

    // Only call setUserNames when collaboratorNameMap adds keys we don't already have (avoids unnecessary updates)
    const hasNewFromCollabs = Object.keys(collaboratorNameMap).some((k) => userNamesRef.current[k] !== collaboratorNameMap[k]);
    if (hasNewFromCollabs) {
      userNamesRef.current = { ...userNamesRef.current, ...collaboratorNameMap };
      setUserNames((prev) => {
        const next = { ...prev, ...collaboratorNameMap };
        return next;
      });
    } else {
      userNamesRef.current = { ...userNamesRef.current, ...collaboratorNameMap };
    }

    const missing = ids.filter((id) => !userNamesRef.current[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    void (async () => {
      const results = await Promise.allSettled(
        missing.map(async (uid) => {
          const snap = await getDoc(doc(db, 'users', uid));
          if (!snap.exists()) return null;
          const name = (snap.data() as any)?.name;
          return typeof name === 'string' && name.trim() ? { uid, name: name.trim() } : null;
        }),
      );

      if (cancelled) return;
      const updates: Record<string, string> = {};
      for (const r of results) {
        if (r.status !== 'fulfilled' || !r.value) continue;
        updates[r.value.uid] = r.value.name;
      }
      if (Object.keys(updates).length === 0) return;
      userNamesRef.current = { ...userNamesRef.current, ...updates };
      setUserNames((prev) => ({ ...prev, ...updates }));
    })();

    return () => {
      cancelled = true;
    };
  }, [items, trip?.owner, trip?.collaborators]);

  const decisionPrompt = useMemo(() => (items.length > 5 ? 'Pick top 2 together for this day?' : null), [items.length]);

  const toggleTag = (t: MaybeItemTag) => {
    setSelectedTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const handleAdd = async () => {
    if (!canEdit) return;
    if (!userId) {
      Alert.alert('Sign in required', 'Please sign in to add ideas.');
      return;
    }
    const title = newTitle.trim();
    if (!title) return;

    const tagsToAdd = [...selectedTags];

    setCreating(true);
    setNewTitle('');
    setSelectedTags([]);

    if (!isOnline) {
      showToast('Offline — will sync when back online.', 'info');
    }

    try {
      await addMaybeItem({
        title,
        description: undefined,
        source: 'manual',
        sourceId: undefined,
        tags: tagsToAdd,
        createdBy: userId,
      });
      showSuccess('Added to Maybe', 4000);
    } catch (e: any) {
      const isOffline = e?.code === 'unavailable' || e?.code === 'failed-precondition';
      if (isOffline) {
        showToast("You're offline – we'll add this when you're back online.", 'info');
      } else {
        showError(e?.message || 'Failed to add idea.');
        Alert.alert('Error', e?.message || 'Failed to add idea.');
      }
    } finally {
      setCreating(false);
    }
  };

  const schedule = async (item: MaybeItem) => {
    if (!onScheduleItem) {
      showError('Scheduling is not available right now');
      return;
    }
    try {
      const didSchedule = await scheduleItem(item, onScheduleItem);
      if (didSchedule) {
        showSuccess('Added to itinerary!');
      }
    } catch (e: any) {
      Alert.alert('Unable to schedule', e?.message || 'Failed to schedule this idea.');
    }
  };

  const archive = async (item: MaybeItem) => {
    try {
      const didArchive = await archiveItem(item);
      if (!didArchive) {
        return;
      }
    } catch (e: any) {
      Alert.alert('Unable to archive', e?.message || 'Failed to archive this idea.');
    }
  };

  return (
    <Card variant="outlined" style={{ marginTop: theme.spacing(1.5) }}>
      <View style={{ padding: theme.spacing(1.5), gap: theme.spacing(1.25) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
            <Ionicons name="help-circle-outline" size={20} color={theme.colors.primary} />
            <SubHeading>Maybe</SubHeading>
          </View>
          {items.length > 0 && (
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '700' }}>
              {items.length} saved
            </Caption>
          )}
        </View>

        {!!decisionPrompt && (
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            {decisionPrompt}
          </Caption>
        )}

        {canEdit && (
          <View style={{ gap: theme.spacing(0.75) }}>
            <InputField
              placeholder='Add an idea (e.g. "Science museum", "Arcade")'
              value={newTitle}
              onChangeText={setNewTitle}
              onSubmitEditing={() => void handleAdd()}
              returnKeyType="done"
              blurOnSubmit
              editable={canEdit}
            />

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
              {TAGS.map((t) => {
                const selected = selectedTags.includes(t.id);
                return (
                  <Button
                    key={t.id}
                    label={t.label}
                    onPress={() => toggleTag(t.id)}
                    variant={selected ? 'secondary' : 'ghost'}
                    icon={<Ionicons name={t.icon} size={14} color={selected ? theme.colors.primary : theme.colors.textSecondary} />}
                    style={{ paddingHorizontal: theme.spacing(1), paddingVertical: theme.spacing(0.5) }}
                  />
                );
              })}
            </View>

            <Button
              label={creating ? 'Adding…' : 'Add to Maybe'}
              onPress={() => void handleAdd()}
              disabled={!newTitle.trim() || creating}
              variant="secondary"
              style={{ width: '100%' }}
              icon={<Ionicons name="add-outline" size={18} color={theme.colors.primary} />}
            />
            {onOpenIdeas && (
              <Button
                label="Add from ideas"
                onPress={onOpenIdeas}
                variant="ghost"
                size="small"
                style={{ width: '100%' }}
                icon={<Ionicons name="sparkles-outline" size={16} color={theme.colors.primary} />}
              />
            )}
          </View>
        )}

        {loading ? (
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: theme.spacing(1) }}>
            Loading Maybe ideas…
          </Caption>
        ) : items.length === 0 ? (
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: theme.spacing(1) }}>
            Save a few ideas here, then vote and pick the best ones.
          </Caption>
        ) : (
          <View key={`maybe-list-${items.length}-${items[0]?.id ?? 'e'}`} style={{ gap: theme.spacing(1) }}>
            {items.map((it) => (
              <MaybeItemCard
                key={it.id}
                tripId={tripId}
                item={it}
                canEdit={canEdit}
                suggestedByLabel={getSuggestedByLabel(it.createdBy, userId, trip, userNames)}
                onSchedule={() => void schedule(it)}
                onArchive={() => void archive(it)}
                isSaving={isItemCreating(it.id)}
                isScheduling={isItemScheduling(it.id)}
                isArchiving={isItemArchiving(it.id)}
              />
            ))}
          </View>
        )}
      </View>
    </Card>
  );
}
